import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CreditCard, Check, ExternalLink } from "lucide-react";

const PLANS = [
  { id: "starter", name: "Starter", monthly: 7.99, annual: 79.9, props: "1–3 properties" },
  { id: "growth", name: "Growth", monthly: 14.99, annual: 149.9, props: "4–15 properties", popular: true },
  { id: "pro", name: "Pro", monthly: 29.99, annual: 299.9, props: "16–50 properties" },
  { id: "agency", name: "Agency", monthly: 59.99, annual: 599.9, props: "Letting agents · 200 pooled" },
  { id: "portfolio", name: "Portfolio", monthly: 499, annual: 4990, props: "Up to 250 · entities, analytics, board report, bulk import", premium: true },
  { id: "estate", name: "Estate", monthly: 2499, annual: 24990, props: "Up to 1,000 · dedicated manager, API, 4h concierge", premium: true },
  { id: "sovereign", name: "Sovereign", monthly: 7500, annual: 75000, props: "Unlimited · named team, 1h SLA, bespoke reporting", premium: true },
];

export default function Billing() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const highlight = params.get("upgrade") || localStorage.getItem("certy_plan_intent");
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState(null);
  useEffect(() => { if (highlight) document.getElementById("billing-card")?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [highlight]);
  const trialDays = user?.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000)) : 0;

  const choose = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: `${p.id}_${annual ? "yearly" : "monthly"}`, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBusy(null); }
  };
  const portal = async () => {
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Portal unavailable"); }
  };

  return (
    <div id="billing-card" className="bg-card border border-border rounded-2xl p-6" data-testid="billing-card">
      <h2 className="font-heading font-bold mb-1 flex items-center gap-2"><CreditCard size={18} /> Billing</h2>
      {user?.has_subscription ? (
        <div>
          <p className="text-sm text-muted-foreground mb-3" data-testid="billing-status">You're on the <b className="capitalize text-foreground">{user.plan}</b> plan, billed {user.billing_interval === "year" ? "annually" : "monthly"}.
            {user.billing_status === "past_due" && <span className="ml-1 text-rose-600 font-medium">Last payment failed — please update your card.</span>}</p>
          <button data-testid="manage-billing" onClick={portal} className="min-h-[44px] px-4 inline-flex items-center gap-2 rounded-xl bg-navy hover:bg-navy-700 text-white font-semibold transition-colors"><ExternalLink size={16} /> Manage billing</button>
          <p className="mt-2 text-xs text-muted-foreground">Change plan, update card, download invoices or cancel — via Stripe's secure portal.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-3">{trialDays > 0 ? `${trialDays} trial days left — pick a plan now and you won't be charged until the trial ends.` : "Your trial has ended. Choose a plan to keep reminders and Evidence Packs running."}</p>
          <div className="inline-flex rounded-xl border border-border p-1 mb-4 text-sm">
            <button data-testid="interval-monthly" onClick={() => setAnnual(false)} className={`px-3 min-h-[36px] rounded-lg ${!annual ? "bg-navy text-white" : ""}`}>Monthly</button>
            <button data-testid="interval-annual" onClick={() => setAnnual(true)} className={`px-3 min-h-[36px] rounded-lg ${annual ? "bg-navy text-white" : ""}`}>Annual <span className="text-emerald-glow text-xs">2 months free</span></button>
          </div>
          <div className="space-y-2">
            {PLANS.map((p) => (
              <div key={p.id} data-testid={`plan-row-${p.id}`} className={`flex items-center justify-between p-3 rounded-xl border ${highlight === p.id ? "border-emerald-brand ring-2 ring-emerald-brand/30 bg-emerald-50/60" : p.popular ? "border-emerald-brand bg-emerald-50/40" : "border-border"}`}>
                <div><div className="font-semibold text-sm flex items-center gap-2">{p.name} {p.popular && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-brand text-white">Popular</span>}{p.premium && <span className="text-[10px] px-1.5 py-0.5 rounded bg-navy text-amber-300">Private</span>}</div><div className="text-xs text-muted-foreground">{p.props}</div></div>
                <div className="flex items-center gap-3"><span className="font-heading font-bold">£{annual ? p.annual.toFixed(2) : p.monthly.toFixed(2)}<span className="text-xs text-muted-foreground font-normal">/{annual ? "yr" : "mo"}</span></span>
                  <button data-testid={`choose-${p.id}`} disabled={!!busy} onClick={() => choose(p)} className="min-h-[40px] px-3 rounded-lg bg-navy text-white text-sm font-semibold disabled:opacity-60">{busy === p.id ? "Opening…" : "Choose"}</button>
                </div>
              </div>
            ))}
          </div>
          <ul className="mt-3 text-xs text-muted-foreground space-y-1">
            <li className="flex items-center gap-1.5"><Check size={12} className="text-emerald-brand" /> Secure checkout by Stripe · VAT handled · cancel anytime</li>
          </ul>
        </>
      )}
    </div>
  );
}
